import React, {useState} from 'react';
import {Alert, Button} from 'reactstrap';
import {useAuthContext} from '../../context/AuthContext';

/**
 * App-wide auth recovery banner. Shows when the session has expired OR when a
 * sign-in step has failed (lastAuthFailure) — either way the user always has an
 * interactive "Sign in again" path and is never stranded. When a failure was
 * recorded, copyable diagnostics (account, failing step, server reason) are
 * shown so the user can hand them to support.
 */
const SessionExpiredBanner: React.FC = () => {
    const {sessionExpired, lastAuthFailure, recoverSession, logout} =
        useAuthContext();
    const [recovering, setRecovering] = useState(false);
    const [copied, setCopied] = useState(false);

    if (!sessionExpired && !lastAuthFailure) {
        return null; // Auth is healthy => show nothing
    }

    const handleSignIn = async () => {
        setRecovering(true);
        try {
            await recoverSession();
        } finally {
            setRecovering(false);
        }
    };

    const detailLines = lastAuthFailure
        ? [
              lastAuthFailure.email && `Account: ${lastAuthFailure.email}`,
              lastAuthFailure.oid && `Account id: ${lastAuthFailure.oid}`,
              lastAuthFailure.tenantId && `Tenant: ${lastAuthFailure.tenantId}`,
              `Failed step: ${lastAuthFailure.step}`,
              lastAuthFailure.status !== undefined &&
                  `Server response: HTTP ${lastAuthFailure.status}`,
              `Detail: ${lastAuthFailure.detail}`,
              `Time: ${lastAuthFailure.at}`,
              `Site: ${window.location.origin}`
          ].filter((line): line is string => Boolean(line))
        : [];

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(detailLines.join('\n'));
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy sign-in details', err);
        }
    };

    return (
        <Alert color="warning" className="mb-0">
            <div className="text-center">
                {sessionExpired
                    ? 'Your session has expired.'
                    : 'We were unable to log you in.'}{' '}
                <Button
                    color="warning"
                    size="sm"
                    onClick={handleSignIn}
                    disabled={recovering}
                >
                    {recovering ? 'Signing in…' : 'Sign in again'}
                </Button>{' '}
                {/* Escape hatch when re-auth of the cached account keeps
                    failing: full logout clears local + MSAL state and returns
                    to the login page, where the account can be re-picked. */}
                <Button
                    color="link"
                    size="sm"
                    className="p-0 align-baseline"
                    onClick={logout}
                    disabled={recovering}
                >
                    Log out and sign in fresh
                </Button>
            </div>
            {lastAuthFailure && (
                <div className="mt-2 text-center">
                    <div className="small">
                        If this keeps happening, please send these details to
                        support:
                    </div>
                    <pre
                        className="small text-start d-inline-block mb-1 mt-1 p-2 border rounded"
                        style={{whiteSpace: 'pre-wrap', maxWidth: '100%'}}
                    >
                        {detailLines.join('\n')}
                    </pre>
                    <div>
                        <Button
                            color="link"
                            size="sm"
                            className="p-0"
                            onClick={handleCopy}
                        >
                            {copied ? 'Copied!' : 'Copy details'}
                        </Button>
                    </div>
                </div>
            )}
        </Alert>
    );
};

export default SessionExpiredBanner;
